import React from 'react';
import classNameBind from 'classnames/bind';
import _isArray from 'lodash/isArray';
import Transition from 'react-transition-group/Transition';
import ActionList from '../../../ActionList';
import Button from '../../../Button';
import Icon from '../../../Icon';
import Popover from '../../../Popover';
import Checkbox from '../../../Checkbox';
import uuid from '../../../../utils/methods/uuid';
import styles from './ListItem.module.css';
import ExpandableWrapper from '../ExpandableWrapper';
import ExpandableWrapperWithColumns from '../ExpandableWrapperWithColumns';

const bindClasses = classNameBind.bind(styles);

class ListItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isExpanded: false,
    };
  }

  expandOnClick = (event) => {
    const { onExpandToggle } = this.props;
    event.stopPropagation();
    if (onExpandToggle) {
      onExpandToggle(!this.state.isExpanded);
    }
    this.expandChange(!this.state.isExpanded);
  };

  expandChange = (value) => {
    this.setState({
      isExpanded: value,
    });
  };

  selectOnChange = (event, item) => {
    const { withSelect } = this.props;

    if (withSelect.selectOnClick) {
      withSelect.selectOnClick(item);
    }

    if (withSelect.onChange) {
      withSelect.onChange(event);
    }
  };

  expandPrefix = (child, index) => (
    <div
      data-ppuc-id="List.Item.expandable"
      className={bindClasses(
        'column',
        'expand-margin',
        `flex-${this.props.columns[index].size}`,
      )}
      key={index}
    >
      <Icon
        name={`chevron-${this.state.isExpanded ? 'down' : 'right'}`}
        className={bindClasses('icon', { hidden: this.props.isLoading })}
      />
      <div className={bindClasses('expand-text-margin')}>
        {child}
      </div>
    </div>
  );

  selectPrefix = (child, index) => {
    const {
      children,
      withSelect,
      columns,
      id,
    } = this.props;

    return (
      <div
        className={bindClasses('column', `flex-${columns[index].size}`)}
        key={withSelect.value || uuid()}
        data-ppuc-id="List.Item.selectable"
      >
        <Checkbox
          id={id || withSelect.value}
          name={withSelect.name}
          onChange={event => this.selectOnChange(event, children)}
          value={withSelect.value}
          checked={withSelect.checked}
          disabled={withSelect.disabled}
        />
        <div className={bindClasses('select-text-margin')}>
          {child}
        </div>
      </div>
    );
  };

  actionsSuffix = (child, index) => {
    const groups = this.props.withActions.items.length !== 0
      ? [
        {
          id: '1',
          items: this.props.withActions.items,
        },
      ]
      : [];

    return (
      <div
        className={bindClasses('column', 'action-justify', `flex-${this.props.columns[index].size}`)}
        key={uuid()}
        data-ppuc-id="List.Item.withAction"
      >
        {child}
        <Popover
          position="bottom-end"
          icon="actions"
          openProps={{ active: true }}
          activatorElement={(<Button icon="actions" appearance="simple" onClick={() => {}} />)}
          dataId="List.Item.withAction.popover"
        >
          <ActionList
            groups={groups}
            onItemSelected={this.props.withActions.onItemSelected}
          />
        </Popover>
      </div>
    );
  }

  expandedContent = () => {
    const lastChild = this.props.children[this.props.children.length - 1];

    return this.state.isExpanded && (lastChild.type === ExpandableWrapper || lastChild.type === ExpandableWrapperWithColumns) && (
      <div
        className={bindClasses('extended-content')}
        role="button"
        tabIndex={0}
        key={uuid()}
        onClick={e => e.stopPropagation()}
      >
        <Transition in={this.state.isExpanded} timeout={0}>
          {status => (
            <div className={bindClasses('content', `content-${status}`)}>
              {lastChild}
            </div>
          )}
        </Transition>
      </div>
    );
  };

  commonColumn = (child, index) => (
    this.props.columns[index]
    && (
      <div
        className={bindClasses(
          'column',
          `align-content-${this.props.columns[index].align}`,
          `flex-${this.props.columns[index].size}`,
        )}
        key={
          typeof this.props.columns[index].key !== 'undefined'
            ? `${this.props.columns[index].key}_${index}`
            : uuid()
        }
        data-ppuc-id="List.Item.cell"
      >
        {child}
      </div>
    )
  );

  detectAdditionalRowOptions = (index) => {
    const { children, withExpand, withSelect, withActions } = this.props;

    if (index === 0) {
      if (withExpand) return this.expandPrefix;
      if (withSelect) return this.selectPrefix;

      return this.commonColumn;
    }

    if (withActions) {
      if (withExpand && index === children.length - 2) {
        return this.actionsSuffix;
      }

      if (!withExpand && index === children.length - 1) {
        return this.actionsSuffix;
      }
    }

    return this.commonColumn;
  };

  render() {
    const {
      itemOnClick,
      tabIndex,
      children,
      className,
      blackLink,
      background,
      withExpand,
    } = this.props;

    return (
      <>
        <div
          onClick={(e) => {
            if (
              withExpand
              && !['INPUT', 'A'].includes(e.target.tagName)
              && !(typeof (e.target.className) === 'string' && e.target.className.includes('Button'))
            ) {
              this.expandOnClick(e);
            }
            if (itemOnClick) itemOnClick(e);
          }}
          role="button"
          tabIndex={tabIndex}
          className={`${bindClasses(
            'row',
            background,
            itemOnClick || withExpand ? 'clickable' : null,
            blackLink ? 'black-link' : null,
          )} ${className}`}
          data-ppuc-id="List.Item.row"
        >
          {_isArray(children) ? children.map((child, index) => (
            this.detectAdditionalRowOptions(index)(child, index)
          )) : this.detectAdditionalRowOptions(0)(children, 0)}
        </div>
        {this.expandedContent()}
      </>
    );
  }
}

ListItem.defaultProps = {
  columns: [],
  itemOnClick: null,
  withExpand: false,
  withSelect: null,
  withActions: null,
  tabIndex: 0,
  className: '',
  id: undefined,
  blackLink: true,
  background: 'white',
};

ListItem.displayName = 'List.Item';

export default ListItem;
